import { Box } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import CustomSearchBox from 'components/app-container/custom-search-box/CustomSearchBox';
import { UPDataGrid } from 'components/data-grid';
import DeleteModal from 'components/delete-modal';
import { useGridSearch } from 'hooks/useGridSearch';
import dayjs from 'utils/dayjs';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store';
import { deleteMotiveExceptionAction } from 'store/actions/motive-exception-actions';
import { activeEmployeeDropdownSelector } from 'store/selectors/employee-selector';
import { locationsDropdownItemsSelector } from 'store/selectors/company-locations-selectors';
import { payrollSelector } from 'store/selectors/payroll-selectors';
import { toNumber } from 'lodash';
import { useParams } from 'react-router-dom';
import {
  CONTRACTS__ARE_YOU_SURE,
  MOTIVE_EXCEPTION__COLUMN_ACTIONS,
  MOTIVE_EXCEPTION__COLUMN_END_DATE,
  MOTIVE_EXCEPTION__COLUMN_MOTIVE,
  MOTIVE_EXCEPTION__COLUMN_START_DATE,
  MOTIVE_EXCEPTION__COLUMN_TARGETS,
  MOTIVE_EXCEPTION__COLUMN_TYPE,
  MOTIVE_EXCEPTION__END_DATE_OPEN_ENDED,
  MOTIVE_EXCEPTION__TYPE_EMPLOYEE,
  MOTIVE_EXCEPTION__TYPE_LOCATION,
} from 'translations/constants';
import { MotiveExceptionType } from 'types/company/payroll/IMotiveException';
import { ReactComponent as DeleteIcon } from '../../../../assets/icons/DeleteGrey.svg';
import { ReactComponent as EditIcon } from '../../../../assets/icons/EditGrey.svg';
import { MotiveExceptionsGridProps } from './types';

const MotiveExceptionsGrid = (props: MotiveExceptionsGridProps): JSX.Element => {
  const { motiveExceptions, onEdit } = props;
  const [t] = useTranslation();
  const dispatch = useAppDispatch();
  const { companyId } = useParams();

  const employees = useAppSelector(activeEmployeeDropdownSelector);
  const locations = useAppSelector(locationsDropdownItemsSelector);
  const payroll = useAppSelector(payrollSelector);

  const [rowOnHover, setRowOnHover] = useState<number>();
  const [idToDelete, setIdToDelete] = useState<number>();
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

  const resolveTargetsLabel = useCallback(
    (type: MotiveExceptionType, targetIds: number[]) => {
      const options = type === MotiveExceptionType.EMPLOYEE ? employees : locations;
      return targetIds
        .map(id => options.find(o => toNumber(o.value) === id)?.label ?? id)
        .filter(Boolean)
        .join(', ');
    },
    [employees, locations],
  );

  const resolveMotiveLabel = useCallback(
    (contractMotiveId: number) => {
      const motive = payroll?.contractMotives?.find(cm => cm.code === contractMotiveId);
      return motive ? t(motive.nameConstant) : contractMotiveId;
    },
    [payroll, t],
  );

  const rows = useMemo(
    () =>
      motiveExceptions.map(me => ({
        id: me.id,
        typeLabel: t(
          me.type === MotiveExceptionType.EMPLOYEE ? MOTIVE_EXCEPTION__TYPE_EMPLOYEE : MOTIVE_EXCEPTION__TYPE_LOCATION,
        ),
        targetsLabel: resolveTargetsLabel(me.type, me.targetIds),
        motiveLabel: resolveMotiveLabel(me.contractMotiveId),
        startDateLabel: dayjs(me.startDate).format('DD/MM/YYYY'),
        endDateLabel: me.endDate ? dayjs(me.endDate).format('DD/MM/YYYY') : t(MOTIVE_EXCEPTION__END_DATE_OPEN_ENDED),
        raw: me,
      })),
    [motiveExceptions, resolveMotiveLabel, resolveTargetsLabel, t],
  );

  const matches = useCallback((row: typeof rows[number], term: string) => {
    const needle = term.toLowerCase();
    return (
      row.typeLabel.toLowerCase().includes(needle) ||
      row.targetsLabel.toLowerCase().includes(needle) ||
      row.motiveLabel.toString().toLowerCase().includes(needle) ||
      row.startDateLabel.toLowerCase().includes(needle) ||
      row.endDateLabel.toLowerCase().includes(needle)
    );
  }, []);

  const { onSearch, filteredRows } = useGridSearch(rows, matches);

  const onDeleteConfirmed = useCallback(() => {
    if (idToDelete && companyId) {
      dispatch(deleteMotiveExceptionAction({ companyId: toNumber(companyId), id: idToDelete }));
    }
    setOpenDeleteModal(false);
  }, [companyId, dispatch, idToDelete]);

  const columns: GridColDef[] = [
    { field: 'id', headerName: '', hide: true },
    { field: 'typeLabel', headerName: t(MOTIVE_EXCEPTION__COLUMN_TYPE), width: 130, sortable: false },
    { field: 'targetsLabel', headerName: t(MOTIVE_EXCEPTION__COLUMN_TARGETS), width: 260, sortable: false },
    { field: 'motiveLabel', headerName: t(MOTIVE_EXCEPTION__COLUMN_MOTIVE), width: 180, sortable: false },
    { field: 'startDateLabel', headerName: t(MOTIVE_EXCEPTION__COLUMN_START_DATE), width: 130, sortable: false },
    { field: 'endDateLabel', headerName: t(MOTIVE_EXCEPTION__COLUMN_END_DATE), width: 130, sortable: false },
    {
      field: 'actions',
      headerName: t(MOTIVE_EXCEPTION__COLUMN_ACTIONS),
      align: 'right',
      width: 100,
      sortable: false,
      disableColumnMenu: true,
      renderCell: params => {
        if (rowOnHover === params.id) {
          return (
            <div>
              <EditIcon
                className="motiveExceptionIcon"
                onClick={() => {
                  const found = motiveExceptions.find(me => me.id === rowOnHover);
                  if (found) onEdit(found);
                }}
              />
              <DeleteIcon
                onClick={() => {
                  setIdToDelete(rowOnHover as number);
                  setOpenDeleteModal(true);
                }}
                className="motiveExceptionIcon"
              />
            </div>
          );
        }
        return null;
      },
    },
  ];

  return (
    <Box>
      <Box sx={{ marginBottom: '16px', display: 'flex' }}>
        <CustomSearchBox onSearch={onSearch} />
      </Box>
      <Box sx={{ height: '400px', width: '100%' }}>
        <UPDataGrid
          rows={filteredRows}
          columns={columns}
          setRowOnHover={setRowOnHover}
          getRowId={r => r.id}
          withCheckboxes={false}
        />
      </Box>
      {openDeleteModal && (
        <DeleteModal
          content={t(CONTRACTS__ARE_YOU_SURE)}
          open={openDeleteModal}
          onClose={() => setOpenDeleteModal(false)}
          onDelete={onDeleteConfirmed}
        />
      )}
    </Box>
  );
};

export default MotiveExceptionsGrid;
